import React, { useState, useEffect, useContext, useRef } from "react";
import { Box } from "@material-ui/core";
import {
  ContactInfoWorkflow,
  IContactInfo,
  TravelerSelectStep,
  ContactInfoStep,
  Header,
  MobilePopoverCard,
  BackButton,
  MobileFloatingButton,
  LoadingPopup,
  B2BSpinner,
  emailRegex,
  phoneRegex,
  Icon,
  IconName,
  InformationalModal as TreesModal,
  EarnOfferBanner,
  getHSPEarnOfferDescription,
  NotificationBanner,
  BannerSeverity,
} from "halifax";
import { ADD_CONTACT_INFO, IPerson } from "redmond";
import { RouteComponentProps } from "react-router-dom";

import "./styles.scss";
import clsx from "clsx";
import queryStringParser from "query-string";
import {
  CDN_ASSETS,
  getConfigTenant,
  isVesperTenant,
  useIsTravelApp,
  useQueryParams,
} from "@capone/common";
import { MobileVacationRentalsBookWorkflowConnectorProps } from "./container";
import {
  PaymentCard,
  VacationRentalsPriceBreakdownDropdown,
  AGENT_FEE,
} from "../..";
import {
  CONTACT_INFO_SAVE,
  CONTINUE,
  CONTACT_INFO_TITLE,
  CONTACT_INFO_SUBTITLE,
  REVIEW_MY_TRIP_TEXT,
  PRICE_QUOTE_MESSAGE,
  TREES_MODAL_CTA_TEXT,
  TREES_MODAL_HEADER,
  TREES_MODAL_SUBTITLE,
  TREES_MODAL_TITLE,
  TREES_BOLDED_MODAL_CTA_TEXT,
  LOADING_TRAVELER_MESSAGE,
  CONTACT_INFO_REVIEW_TITLE,
} from "./textConstants";
import {
  PATH_STAYS,
  PATH_VACATION_RENTALS_BOOK_CONFIRMATION,
} from "../../../../../utils/paths";
import { ClientContext } from "../../../../../App";
import { VACATION_RENTALS_REWARDS_DISABLED_BANNER_TEXT } from "../../../../rewards/components/RewardsBanner/constants";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import { goBackToShop } from "../../../../shop/utils/queryStringHelpers";
import { fetchCustomerDetails } from "../../../../../api/v0/customer/fetchCustomerDetails";
import { SelectedVacationRentalSummary } from "../../SelectedVacationRentalSummary";
import { VacationRentalPriceBreakdown } from "../../VacationRentalPriceBreakdownCard";
import { VacationRentalBookPassengerSelection } from "../../VacationRentalBookPassengerSelection";
import { VacationRentalBookMobileButton } from "../../VacationRentalBookMobileButton";
import { TitleSummaryCard } from "../../TitleSummary";
import { VacationRentalTerms } from "../../../../common/VacationRentalTerms/component";
import { VacationRentalImportantInformation } from "../../../../common/VacationRentalImportantInformation/component";
import { CallState } from "../../../../rewards/reducer";
import { VacationRentalTermsOfServiceLink } from "../../VacationRentalTermsOfServiceLink";
import {
  AVAILABLE,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../../context/experiments";
import { getExperimentVariant } from "@capone/experiments";
import dayjs from "dayjs";
import { resolveBookStepSync } from "../../../utils/bookStepSync";
import { usePersistMainGuest } from "../../../utils/usePersistMainGuest";

export interface IMobileVacationRentalsBookWorkflowProps
  extends MobileVacationRentalsBookWorkflowConnectorProps,
    RouteComponentProps {
  isTreesModalExperiment: boolean;
  isTravelWalletOfferExperiment: boolean;
  isTravelWalletCreditsExperiment: boolean;
  isCreditAndOfferStackingExperimentV1: boolean;
  isTravelCreditHistoryExperiment: boolean;
  primaryTraveler?: IPerson;
}

export enum MobileVacationRentalsBookWorkflowStep {
  TravelerInformation,
  ContactInformation,
  RewardsAndPayment,
  Review,
}

export const MobileVacationRentalsBookWorkflow = ({
  priceQuote,
  hasNoUserPassengers,
  scheduleVacationRentalPriceQuote,
  history,
  vrReservation,
  isBookingValid,
  setContactInfo,
  confirmationEmail,
  confirmationPhoneNumber,
  priceDifferenceAcknowledged,
  scheduleVacationRentalsBook,
  priceQuoteInProgress,
  priceQuoteErrors,
  confirmationDetailsErrors,
  fetchApplicableTravelWalletItems,
  resetPaymentCardSelectedAccounts,
  clearVacationRentalsPriceQuote,
  setTravelerInfoCommitted,
  resetAddMainGuestCallState,
  fetchTravelWalletCreditHistory,
  isTreesModalExperiment,
  isTravelWalletOfferExperiment,
  isTravelCreditHistoryExperiment,
  primaryTraveler,
  setUserSelectedPassengerIds,
  selectedHome,
  userSelectedTraveler,
  addMainGuest,
  userPassengerCallState,
  offerToApply,
}: IMobileVacationRentalsBookWorkflowProps) => {
  const clientContext = useContext(ClientContext);
  const isVesper = isVesperTenant(getConfigTenant());
  const { sessionInfo, isAgentPortal, isRewardsDisabled } = clientContext;

  const [contactInfo, setContact] = useState<IContactInfo | null>({
    phoneNumber: confirmationPhoneNumber || "",
    email: confirmationEmail || sessionInfo?.userInfo?.email || "",
  });
  const [customerDetailsLoading, setCustomerDetailsLoading] =
    React.useState<boolean>(true);
  const [checkoutStep, setCheckoutStep] =
    useState<MobileVacationRentalsBookWorkflowStep>(
      MobileVacationRentalsBookWorkflowStep.TravelerInformation,
    );
  const [travelerWorkflowStep, setTravelerWorkflowStep] =
    useState<TravelerSelectStep>(TravelerSelectStep.Main);
  const [contactInfoStep, setContactInfoStep] = useState<ContactInfoStep>(
    ContactInfoStep.Main,
  );

  const [openPaymentCard, setOpenPaymentCard] = useState<boolean>(false);
  const [treeModalOpen, setTreeModalOpen] = useState(false);
  const [isPaymentUpdateNeeded, setIsPaymentUpdateNeeded] = useState(false);
  // Latches true once the initial load has fully settled (see the init gate before
  // the render). Keeps the one-time loading screen from re-triggering on re-quotes.
  const [hasInitialized, setHasInitialized] = useState(false);

  const [travelerToEdit, setTravelerToEdit] = useState<IPerson | undefined>(
    undefined,
  );

  const params = useQueryParams();

  const { isTravelApp } = useIsTravelApp();

  const expState = useExperiments();

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const onBookVacationRental = () => {
    const queryString = queryStringParser.parse(history.location.search);
    priceDifferenceAcknowledged || !!priceQuote
      ? scheduleVacationRentalsBook({
          agentFee: isAgentPortal ? AGENT_FEE : 0,
          isRecommended: queryString.recommended === "true",
          initialTime: dayjs().toISOString(),
        })
      : scheduleVacationRentalPriceQuote({
          history,
          emailAddress: confirmationEmail ?? "",
          agentFee: isAgentPortal ? AGENT_FEE : 0,
        });
  };

  const openVacationRentalBookPassengerSelection = () =>
    setTravelerWorkflowStep(TravelerSelectStep.TravelerSelect);

  const openContactInfoWorkflow = () => {
    setContactInfoStep(ContactInfoStep.ContactInfoForm);
  };

  const openPaymentSelection = () => setOpenPaymentCard(true);

  // Mobile has no freeze/unfreeze UI, so step position *is* the commit state:
  // sitting on the traveler or contact step means the details are still being
  // edited. Deriving it (rather than setting it from the contact-save handler)
  // keeps it correct when the step is restored from the `bookStep` URL param,
  // which would otherwise skip the commit and strand the user on Review.
  const travelerStepInProgress =
    checkoutStep ===
      MobileVacationRentalsBookWorkflowStep.TravelerInformation ||
    checkoutStep === MobileVacationRentalsBookWorkflowStep.ContactInformation;

  usePersistMainGuest({
    priceQuote,
    email: confirmationEmail,
    phoneNumber: confirmationPhoneNumber,
    userSelectedTraveler,
    addMainGuest,
    priceQuoteInProgress,
    travelerInfoCommitted: !travelerStepInProgress,
  });

  useEffect(() => {
    setTravelerInfoCommitted(!travelerStepInProgress);
    if (travelerStepInProgress) {
      resetAddMainGuestCallState();
    }
  }, [travelerStepInProgress]);

  // In the travel app, the step whose history entry sits right before Review,
  // so a redirect off Review can pop back to it instead of rewriting Review's
  // entry (which leaves a duplicate entry that swallows a back press).
  const stepBeforeReview = useRef<MobileVacationRentalsBookWorkflowStep>();
  const awaitingStepBack = useRef(false);

  const incrementCheckoutStep = () => {
    const searchParams = new URLSearchParams(history.location.search);
    setCheckoutStep((step) => {
      // Review stays on Review; pushing it again added a duplicate entry.
      if (isTravelApp && step !== MobileVacationRentalsBookWorkflowStep.Review) {
        searchParams.set("bookStep", (step + 1).toString());
        if (step + 1 === MobileVacationRentalsBookWorkflowStep.Review) {
          stepBeforeReview.current = step;
        }

        // No `state`: only the entry the shop pushed is marked `fromVrShop`, so
        // goBackToShop never pops a step entry thinking it's the shop.
        history.push({
          pathname: history.location.pathname,
          search: searchParams.toString(),
        });
      }

      return step === MobileVacationRentalsBookWorkflowStep.Review
        ? step
        : step + 1;
    });
  };

  // In the travel app the bookStep URL param mirrors checkoutStep so native
  // back/forward moves between steps (see resolveBookStepSync).
  const lastBookStepParam = useRef<string>();
  useEffect(() => {
    if (!isTravelApp) {
      return;
    }

    const replaceBookStep = (step: MobileVacationRentalsBookWorkflowStep) => {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set("bookStep", step.toString());

      history.replace({
        ...history.location,
        search: searchParams.toString(),
      });
    };

    const bookStepParam =
      typeof params?.bookStep === "string" ? params.bookStep : undefined;
    const sync = resolveBookStepSync({
      bookStepParam,
      previousBookStepParam: lastBookStepParam.current,
      checkoutStep,
      hasSelectedTraveler: userSelectedTraveler != null,
      travelerStep: MobileVacationRentalsBookWorkflowStep.TravelerInformation,
      restartStep: MobileVacationRentalsBookWorkflowStep.TravelerInformation,
    });
    lastBookStepParam.current = bookStepParam;

    switch (sync.kind) {
      case "writeUrl":
        replaceBookStep(sync.step);
        break;
      case "followUrl":
        setCheckoutStep(sync.step);
        break;
      case "restart":
        replaceBookStep(sync.step);
        setCheckoutStep(sync.step);
        break;
      default:
        break;
    }
  }, [checkoutStep, params?.bookStep, isTravelApp, userSelectedTraveler]);

  const handleGoBack = () => {
    switch (checkoutStep) {
      case MobileVacationRentalsBookWorkflowStep.TravelerInformation:
        if (selectedHome) {
          goBackToShop({ history });
        } else {
          history.push(PATH_STAYS);
        }
        break;
      case MobileVacationRentalsBookWorkflowStep.RewardsAndPayment:
        setCheckoutStep(
          (_) => MobileVacationRentalsBookWorkflowStep.TravelerInformation,
        );
        break;
      case MobileVacationRentalsBookWorkflowStep.Review:
        setCheckoutStep(
          (_) => MobileVacationRentalsBookWorkflowStep.RewardsAndPayment,
        );
        break;
      default:
        setCheckoutStep((step) => step - 1);
        break;
    }
  };
  const closeAllPopovers = () => {
    setTravelerWorkflowStep(TravelerSelectStep.Main);
    setContactInfoStep(ContactInfoStep.Main);
    setOpenPaymentCard(false);
  };

  const requoteAndResetPayment = (emailAddress?: string) => {
    scheduleVacationRentalPriceQuote({
      history,
      emailAddress: emailAddress ?? confirmationEmail ?? "",
      agentFee: isAgentPortal ? AGENT_FEE : 0,
    });
    resetPaymentCardSelectedAccounts();
  };

  const startSchedulePriceQuoteAndResetPayment = (emailAddress?: string) => {
    requoteAndResetPayment(emailAddress);
    setIsPaymentUpdateNeeded(true);
  };

  // Whether the open traveler picker was opened from Review, so a dismissal
  // (browser back in the travel app) can be handled like the picker's Back.
  const travelerPickerOpenedOnReview = useRef(false);
  useEffect(() => {
    travelerPickerOpenedOnReview.current =
      travelerWorkflowStep !== TravelerSelectStep.Main &&
      (travelerPickerOpenedOnReview.current ||
        checkoutStep === MobileVacationRentalsBookWorkflowStep.Review);
  }, [travelerWorkflowStep]);

  useEffect(() => {
    if (priceQuoteErrors.length > 0 || confirmationDetailsErrors.length > 0) {
      setCheckoutStep(
        MobileVacationRentalsBookWorkflowStep.TravelerInformation,
      );
    }
  }, [priceQuoteErrors.length, confirmationDetailsErrors.length]);

  useEffect(() => {
    switch (checkoutStep) {
      case MobileVacationRentalsBookWorkflowStep.TravelerInformation:
        closeAllPopovers();
        openVacationRentalBookPassengerSelection();
        break;
      case MobileVacationRentalsBookWorkflowStep.ContactInformation:
        closeAllPopovers();
        openContactInfoWorkflow();
        break;
      case MobileVacationRentalsBookWorkflowStep.RewardsAndPayment:
        closeAllPopovers();
        openPaymentSelection();
        break;
      case MobileVacationRentalsBookWorkflowStep.Review:
        closeAllPopovers();
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        break;
      default:
        break;
    }
  }, [checkoutStep]);

  // note: it handles the PassengerSelection step logic separately from the previous useEffect;
  // when travelerWorkflowStep is being changed too rapidly (e.g.: changing from Main -> TravelerSelect -> TravelerInfoForm because of hasUserPassengers value)
  // it seems to cause some unexpected behaviours on TravelerSelectWorkflow, which in turn causes the Review screen to be unscrollable
  // https://hopper-jira.atlassian.net/browse/BP-1090
  useEffect(() => {
    if (
      checkoutStep ===
        MobileVacationRentalsBookWorkflowStep.TravelerInformation &&
      hasNoUserPassengers
    ) {
      setTravelerWorkflowStep(TravelerSelectStep.TravelerInfoForm);
    }
  }, [checkoutStep, hasNoUserPassengers]);

  useEffect(() => {
    if (vrReservation) {
      const search = queryStringParser.stringify({
        isTravelApp,
      });
      history.push({
        pathname: PATH_VACATION_RENTALS_BOOK_CONFIRMATION,
        search,
      });
    }
  }, [vrReservation]);

  React.useEffect(() => {
    const getCustomerDetails = async () => {
      try {
        const details = await fetchCustomerDetails();
        setContact({
          email: "",
          ...contactInfo,
          phoneNumber: details?.phoneNumber || "",
        });
        setContactInfo(contactInfo?.email || "", details?.phoneNumber || "");
        setCustomerDetailsLoading(true);
      } finally {
        setCustomerDetailsLoading(false);
      }
    };

    getCustomerDetails();
    isTravelCreditHistoryExperiment && fetchTravelWalletCreditHistory();
  }, []);

  // Requires selectedHome to build the offers request. On a direct page load
  // it hasn't loaded from the URL yet, so wait for it instead of firing on
  // mount (see initialPriceQuoteScheduled below for the same race).
  const applicableTravelWalletItemsFetched = useRef(false);
  useEffect(() => {
    if (
      isTravelWalletOfferExperiment &&
      selectedHome &&
      !applicableTravelWalletItemsFetched.current
    ) {
      applicableTravelWalletItemsFetched.current = true;
      fetchApplicableTravelWalletItems();
    }
  }, [isTravelWalletOfferExperiment, selectedHome]);

  const initialPriceQuoteScheduled = useRef(false);
  useEffect(() => {
    if (
      !customerDetailsLoading &&
      confirmationEmail &&
      selectedHome &&
      !initialPriceQuoteScheduled.current
    ) {
      initialPriceQuoteScheduled.current = true;
      scheduleVacationRentalPriceQuote({
        history,
        emailAddress: confirmationEmail,
        agentFee: isAgentPortal ? AGENT_FEE : 0,
        pollQuoteOnly: true,
      });
    }
  }, [customerDetailsLoading, confirmationEmail, selectedHome]);

  // Declared before the redirect so a step change resets this first; otherwise
  // a redirect fired in the same render would have its guard cleared.
  useEffect(() => {
    awaitingStepBack.current = false;
  }, [checkoutStep]);

  useEffect(() => {
    if (
      checkoutStep === MobileVacationRentalsBookWorkflowStep.Review &&
      !priceQuoteInProgress &&
      !!priceQuote &&
      isPaymentUpdateNeeded
    ) {
      if (
        isTravelApp &&
        stepBeforeReview.current === MobileVacationRentalsBookWorkflowStep.RewardsAndPayment
      ) {
        if (!awaitingStepBack.current) {
          awaitingStepBack.current = true;
          history.goBack();
        }
      } else {
        setCheckoutStep(MobileVacationRentalsBookWorkflowStep.RewardsAndPayment);
      }
    }
  }, [checkoutStep, priceQuoteInProgress, priceQuote, isPaymentUpdateNeeded]);

  useEffect(() => {
    if (priceQuoteInProgress && !!priceQuote) {
      clearVacationRentalsPriceQuote();
    }
  }, [priceQuoteInProgress]);

  useEffect(() => {
    if (
      !customerDetailsLoading &&
      sessionInfo &&
      checkoutStep === MobileVacationRentalsBookWorkflowStep.TravelerInformation
    ) {
      if (primaryTraveler && contactInfo?.email && contactInfo?.phoneNumber) {
        setUserSelectedPassengerIds({
          userSelectedPassengerIds: [primaryTraveler.id],
        });
      }
    }
  }, [
    primaryTraveler,
    customerDetailsLoading,
    contactInfo,
    sessionInfo,
    checkoutStep,
  ]);

  // A single, gap-free loading gate for the initial entry into checkout. Without
  // it the traveler step flashes in and out as each dependency resolves at its own
  // time: availability hydration (page-level), the userInfo/customer-details fetch,
  // the traveler list, and the first price quote. We hold one loader until they've
  // all settled, then latch (hasInitialized) so later re-quotes don't re-trigger it.
  const travelersLoading =
    userPassengerCallState === CallState.NotCalled ||
    userPassengerCallState === CallState.InProcess;

  // We can only schedule a quote when we have an email — the confirmation email, or
  // the userInfo email we seed it from. If we do, hold the loader until the first
  // quote settles; if we don't, there's nothing to wait on.
  const hasQuoteableEmail = !!(
    confirmationEmail || sessionInfo?.userInfo?.email
  );

  const initialQuotePending =
    hasQuoteableEmail &&
    priceQuoteErrors.length === 0 &&
    (priceQuoteInProgress || !priceQuote);

  const initialLoadComplete =
    !!selectedHome &&
    !customerDetailsLoading &&
    !travelersLoading &&
    !initialQuotePending;

  useEffect(() => {
    if (!hasInitialized && initialLoadComplete) {
      setHasInitialized(true);
    }
  }, [hasInitialized, initialLoadComplete]);

  const isInitializing = !hasInitialized;
  const initLoadingMessage =
    travelersLoading || customerDetailsLoading
      ? LOADING_TRAVELER_MESSAGE
      : PRICE_QUOTE_MESSAGE;

  if (!selectedHome) {
    return null;
  }

  return (
    <>
      <Box
        className={clsx(
          "mobile-vacation-rentals-book-workflow-root",
          "display-room-details-modal",
          {
            redesigned: isVesper,
            "mobile-review-vacation-rentals-book":
              checkoutStep === MobileVacationRentalsBookWorkflowStep.Review,
            xmod: isXmodRedesign,
            "travel-app": isTravelApp,
          },
        )}
      >
        <Header
          className={clsx("mobile-vacation-rentals-book-header", "checkout")}
          left={
            isTravelApp ? undefined : (
              <BackButton
                className="mobile-vacation-rentals-book-header-go-back"
                onClick={handleGoBack}
              />
            )
          }
          right={
            <VacationRentalsPriceBreakdownDropdown
              popoverClassName={
                checkoutStep === MobileVacationRentalsBookWorkflowStep.Review
                  ? "mobile-review-vacation-rentals-book-price-breakdown"
                  : undefined
              }
            />
          }
          isMobile
          fullWidth
        />
        <TitleSummaryCard isMobile isXmodRedesign={isXmodRedesign} />
        <SelectedVacationRentalSummary isMobile />
        <VacationRentalBookPassengerSelection
          progress={travelerWorkflowStep}
          setProgress={setTravelerWorkflowStep}
          onDismiss={(travelersChanged) => {
            if (!travelerPickerOpenedOnReview.current || !travelersChanged) {
              return;
            }
            // Same as the picker's Back on Review. If the step already moved
            // off Review, the user is on an earlier step and continues to
            // payment from there, so don't also bounce them back to it.
            if (checkoutStep === MobileVacationRentalsBookWorkflowStep.Review) {
              startSchedulePriceQuoteAndResetPayment();
            } else {
              requoteAndResetPayment();
            }
          }}
          onGoBack={(travelersChanged) => {
            switch (checkoutStep) {
              case MobileVacationRentalsBookWorkflowStep.TravelerInformation:
                handleGoBack();
                break;
              case MobileVacationRentalsBookWorkflowStep.Review:
                // note: when the user changes passengers on the final step and then clicks the go-back button,
                // handle it as if the user clicked continue;
                if (travelersChanged) {
                  startSchedulePriceQuoteAndResetPayment();
                }
                break;
              default:
                break;
            }
          }}
          onContinue={(travelersChanged) => {
            switch (checkoutStep) {
              case MobileVacationRentalsBookWorkflowStep.Review:
                if (travelersChanged) {
                  startSchedulePriceQuoteAndResetPayment();
                }
                break;
              default:
                incrementCheckoutStep();
                break;
            }
          }}
          selectionScreenHeaderElement={
            <VacationRentalsPriceBreakdownDropdown />
          }
          className="b2b"
          onReviewStep={
            checkoutStep === MobileVacationRentalsBookWorkflowStep.Review
          }
          isMobile
          useLocalId
          travelerToEdit={travelerToEdit}
          setTravelerToEdit={setTravelerToEdit}
          preselectedUserId={
            !userSelectedTraveler && primaryTraveler
              ? primaryTraveler.id
              : undefined
          }
          isXmodRedesign={isXmodRedesign}
        />
        <ContactInfoWorkflow
          titles={{
            contactInfoTitle:
              checkoutStep === MobileVacationRentalsBookWorkflowStep.Review
                ? CONTACT_INFO_REVIEW_TITLE
                : CONTACT_INFO_TITLE,
            contactInfoSubtitle: CONTACT_INFO_SUBTITLE,
            buttonTitle:
              checkoutStep === MobileVacationRentalsBookWorkflowStep.Review
                ? CONTACT_INFO_SAVE
                : CONTINUE,
          }}
          progressStep={contactInfoStep}
          setProgressStep={setContactInfoStep}
          isMobile
          contactInfo={contactInfo}
          onGoBack={() => {
            if (
              checkoutStep ===
              MobileVacationRentalsBookWorkflowStep.ContactInformation
            ) {
              handleGoBack();
            }
          }}
          onContactInfoChange={(contactInfo) => {
            setContact(contactInfo);
            setContactInfo(contactInfo.email, contactInfo.phoneNumber);
            if (
              contactInfo.email &&
              contactInfo.phoneNumber &&
              emailRegex.test(contactInfo.email) &&
              phoneRegex.test(contactInfo.phoneNumber)
            ) {
              trackEvent({
                eventName: ADD_CONTACT_INFO,
                properties: {},
              });
              switch (checkoutStep) {
                case MobileVacationRentalsBookWorkflowStep.Review:
                  startSchedulePriceQuoteAndResetPayment(contactInfo.email);
                  break;
                default:
                  scheduleVacationRentalPriceQuote({
                    history,
                    emailAddress: contactInfo.email,
                    agentFee: isAgentPortal ? AGENT_FEE : 0,
                  });
                  break;
              }
            }
            incrementCheckoutStep();
          }}
          mobileHeaderElement={<VacationRentalsPriceBreakdownDropdown />}
          className="b2b"
          loading={customerDetailsLoading}
          hideBackButton={isTravelApp}
          useXmodHeadings={isXmodRedesign}
        />

        <Box className="terms-container">
          <VacationRentalTerms
            cancellationPolicy={
              selectedHome.availability.rate?.cancellationPolicy?.policy
            }
            checkInTimes={selectedHome.listing.terms.checkInTimes}
            checkOutTimes={selectedHome.listing.terms.checkOutTimes}
            checkinInformation={selectedHome.listing.terms.checkinInformation}
            petFriendly={selectedHome.listing.terms.petFriendly}
            petsPolicy={selectedHome.listing.terms.petsPolicy}
            paymentPolicy={priceQuote?.rate.paymentPolicy}
          />
        </Box>
        {(selectedHome.listing.terms?.importantInformation ||
          selectedHome.listing.content?.registrationId) && (
          <Box className="important-info-container">
            <VacationRentalImportantInformation
              importantInformation={
                selectedHome.listing.terms.importantInformation
              }
              registrationId={selectedHome.listing.content?.registrationId}
            />
          </Box>
        )}

        <Box className="mobile-hotels-price-breakdown">
          <VacationRentalPriceBreakdown />
        </Box>

        {getHSPEarnOfferDescription(offerToApply) && (
          <EarnOfferBanner isMobile />
        )}

        <VacationRentalTermsOfServiceLink className="mobile-vacation-rentals-terms-of-service" />

        {isRewardsDisabled && (
          <NotificationBanner
            className="rewards-disabled-banner"
            label={VACATION_RENTALS_REWARDS_DISABLED_BANNER_TEXT}
            severity={BannerSeverity.WARNING}
            icon={<Icon className="banner-icon" name={IconName.WarningAlert} />}
          />
        )}
        {isTreesModalExperiment && (
          <TreesModal
            image={CDN_ASSETS.capone.trees.confirmation}
            header={TREES_MODAL_HEADER}
            title={TREES_MODAL_TITLE}
            onClose={() => setTreeModalOpen(false)}
            subtitle={TREES_MODAL_SUBTITLE}
            icon={<Icon className="trees-icon" name={IconName.TreesIcon} />}
            openModal={treeModalOpen}
            setOpenModal={setTreeModalOpen}
            modalLinkCopy={TREES_MODAL_CTA_TEXT}
            modalButtonCopyStartIcon={
              <Icon className="trees-icon" name={IconName.TreesIcon} />
            }
            isMobile
            boldedModalLinkCopy={TREES_BOLDED_MODAL_CTA_TEXT}
          />
        )}

        <VacationRentalBookMobileButton
          hasContactInfo={!!contactInfo}
          openContactInfo={() => {
            setContactInfoStep(ContactInfoStep.ContactInfoForm);
          }}
          isBookingValid={isBookingValid}
          onConfirmAndBook={() => onBookVacationRental()}
          showPaymentStep
          onApplyRewards={() => {}}
        />
      </Box>
      <MobilePaymentCardPopover
        openPaymentCard={openPaymentCard}
        disabled={!isBookingValid}
        onClose={() => setOpenPaymentCard(false)}
        onConfirm={() => {
          setIsPaymentUpdateNeeded(false);
          incrementCheckoutStep();
          setOpenPaymentCard(false);
        }}
        onGoBack={() => {
          if (
            checkoutStep ===
            MobileVacationRentalsBookWorkflowStep.RewardsAndPayment
          ) {
            handleGoBack();
          }
        }}
        mobileHeaderElement={<VacationRentalsPriceBreakdownDropdown />}
        isXmodRedesign={isXmodRedesign}
      />
      <LoadingPopup
        classes={["mobile-price-quote-loading-popup"]}
        open={isInitializing}
        message={initLoadingMessage}
        indicator={B2BSpinner}
        indicatorSize="small"
        verticalAlignment="center"
        fullScreen
      />
      <LoadingPopup
        classes={["mobile-price-quote-loading-popup"]}
        open={hasInitialized && priceQuoteInProgress}
        message={PRICE_QUOTE_MESSAGE}
        indicator={B2BSpinner}
        indicatorSize="small"
        verticalAlignment="center"
        fullScreen
      />
    </>
  );
};

interface IMobilePaymentCardPopoverProps {
  openPaymentCard: boolean;
  disabled?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onGoBack: () => void;
  mobileHeaderElement?: JSX.Element;
  isXmodRedesign?: boolean;
}

const MobilePaymentCardPopover = (props: IMobilePaymentCardPopoverProps) => {
  const {
    openPaymentCard,
    disabled = true,
    onClose,
    onConfirm,
    onGoBack,
    mobileHeaderElement,
    isXmodRedesign,
  } = props;

  const { isTravelApp } = useIsTravelApp();

  return (
    <MobilePopoverCard
      open={openPaymentCard}
      className={clsx("vacation-rentals-payment-card-popup", "b2b")}
      fullScreen
      onClose={onClose}
      topLeftButton={
        !isTravelApp ? (
          <BackButton
            className="vacation-rentals-payment-card-popup-back-button"
            onClick={onGoBack}
          />
        ) : undefined
      }
      headerElement={mobileHeaderElement}
    >
      <PaymentCard isXmodRedesign={isXmodRedesign} />
      {!disabled && (
        <MobileFloatingButton
          className="vacation-rentals-book-review-trip-button"
          onClick={onConfirm}
          wrapperClassName="b2b"
        >
          {REVIEW_MY_TRIP_TEXT}
        </MobileFloatingButton>
      )}
    </MobilePopoverCard>
  );
};
